import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, LockKeyhole } from 'lucide-react';
import { currentPublicBrand } from '../../lib/publicBrand';

export default function HomePage() {
  const brand = currentPublicBrand();

  // Launch scope: the real software starts at /access.
  // Marketing, plan comparisons, workspace illustrations and sales intake are
  // deliberately deferred. Tenant home and invitation routes are unchanged.
  if (brand.isCareDock) {
    return (
      <div className="caredock-platform-theme flex min-h-screen flex-col bg-[#F3FAFF] text-[#0D274E]" data-testid="caredock-homepage">
        <Header minimal />
        {brand.isPreview && (
          <div role="status" data-testid="caredock-design-preview-notice" className="bg-rose-50 px-5 py-3 text-center text-sm font-semibold text-rose-800">
            Design preview only — do not sign in or upload personal records here.
          </div>
        )}

        <main className="flex flex-1 items-center justify-center px-5 py-14 sm:px-8 sm:py-20">
          <section className="mx-auto w-full max-w-xl text-center" aria-labelledby="caredock-welcome-heading">
            <img
              src={brand.wordmark}
              alt="CareDock"
              className="mx-auto h-auto w-full max-w-[245px] sm:max-w-[290px]"
              data-testid="caredock-text-only-wordmark"
            />
            <h1 id="caredock-welcome-heading" className="mt-9 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
              Your care workspace. In one place.
            </h1>
            <p className="mx-auto mt-5 max-w-md text-base leading-7 text-[#526B81]">
              Already using CareDock through your organisation? Use the secure access portal below.
            </p>

            <div className="mt-9 flex justify-center">
              <Link
                to="/access"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#0D274E] px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-[#153B6C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0077B5]"
                data-testid="home-access-portal"
              >
                Access portal <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            <p className="mt-6 text-sm leading-6 text-[#60778B]">
              Considering CareDock for your organisation?{' '}
              <Link to="/contact?type=platform_demo" className="font-semibold text-[#0077B5] underline underline-offset-4 hover:text-[#0D274E]" data-testid="caredock-provider-enquiry-link">
                Provider enquiries
              </Link>
            </p>

            <p className="mx-auto mt-8 flex max-w-md items-start justify-center gap-2 text-sm leading-6 text-[#60778B]">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-[#0077B5]" aria-hidden="true" />
              <span>Private access. Your care organisation controls permissions.</span>
            </p>
          </section>
        </main>

        <Footer minimal />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-950 flex flex-col">
      <Header minimal />

      <main className="flex-1">
        <section className="mx-auto flex min-h-[68vh] max-w-6xl items-center px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white shadow-sm" aria-hidden="true">
              <span className="font-heading text-2xl font-bold">C</span>
            </div>

            <p className="mt-8 text-sm font-semibold uppercase tracking-[0.22em] text-primary">
              CareDock
            </p>
            <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
              Your care workspace. In one place.
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
              A secure, straightforward way for care teams and their partners to manage work.
              Your organisation's workspace opens after you sign in.
            </p>

            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="h-12 rounded-full bg-primary px-7 text-white hover:bg-primary-hover"
                data-testid="home-access-portal"
              >
                <Link to="/access">
                  Sign in
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="mx-auto mt-8 flex max-w-md items-start justify-center gap-2 text-left text-sm leading-6 text-slate-500">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <p>Private, invitation-based access. Your provider controls what you can see and do.</p>
            </div>
          </div>
        </section>
      </main>
      <Footer minimal />
    </div>
  );
}
